import { useCallback } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { COMPLETE_TASK_ERROR_MAP, useCompleteTask } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { TaskType } from 'src/app/pages/starterDashboardV2/shared/hooks';

import type { Task } from '../../../../types';

type UseCompleteTaskByCheckboxReturn = (
	taskId: string,
	taskName: TaskType,
	status?: Task['status'],
) => () => Promise<void>;

export function useCompleteTaskByCheckbox(options?: {
	version?: 'new_12_2025' | 'new_ai_03_2026';
}): UseCompleteTaskByCheckboxReturn {
	const {
		actions: { refreshData },
	} = useStarterDashboardV2PageData();

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { mutate: completeTask } = useCompleteTask({
		onError: (error) => {
			if (error === COMPLETE_TASK_ERROR_MAP.UNAUTHORIZED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
		onSuccess: () => {
			refreshData();
		},
		completionType: 'manual',
		version: options?.version ?? 'new_12_2025',
	});

	return useCallback(
		(taskId: string, taskName: TaskType, status: Task['status'] = 'ACTIVE') => {
			return async () => {
				if (status === 'COMPLETED') return;
				const result = await completeTask(taskId, taskName);
				if (result.status === 'error' && result.error !== COMPLETE_TASK_ERROR_MAP.ALREADY_COMPLETED_ERROR.code) {
					throw new Error(result.error);
				}
			};
		},
		[completeTask],
	);
}
